import React from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type { ColumnDef, StockFeatures } from '@tanstack/react-table';
import {
  Card,
  CardHeader,
  CardContent,
  Flex,
  Heading,
  Text,
  Button,
  Box,
  Select,
  DataTable,
  LoadingOrbit,
  TextField,
  Divider,
  EmptyState,
  type SelectOption,
} from '@chatsdet/ui';
import { Trash, PencilSimple, Plus, Check, X } from '@phosphor-icons/react';
import { API_URL } from '../../config.js';

type ElementType = 'data_table' | 'form' | 'table' | 'button' | 'link' | 'input' | 'other';

type ComponentTestTemplate = {
  id: string;
  elementType: ElementType;
  name: string;
  description: string | null;
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
  updatedBy: string | null;
};

type TemplatesResponse = {
  ok: true;
  templates: Record<ElementType, ComponentTestTemplate[]>;
  elementTypes: ElementType[];
};

const ELEMENT_TYPE_OPTIONS: SelectOption[] = [
  { id: 'data_table', label: 'Data Table' },
  { id: 'form', label: 'Form' },
  { id: 'table', label: 'Table' },
  { id: 'button', label: 'Button' },
  { id: 'link', label: 'Link' },
  { id: 'input', label: 'Input' },
  { id: 'other', label: 'Other' },
];

const ELEMENT_TYPE_LABELS: Record<ElementType, string> = {
  data_table: 'Data Table',
  form: 'Form',
  table: 'Table',
  button: 'Button',
  link: 'Link',
  input: 'Input',
  other: 'Other',
};

export function TestTemplatesTab() {
  const queryClient = useQueryClient();
  const [selectedType, setSelectedType] = React.useState<ElementType>('form');
  const [editingId, setEditingId] = React.useState<string | null>(null);
  const [editName, setEditName] = React.useState('');
  const [editDescription, setEditDescription] = React.useState('');
  const [showAddForm, setShowAddForm] = React.useState(false);
  const [newName, setNewName] = React.useState('');
  const [newDescription, setNewDescription] = React.useState('');

  const templatesQuery = useQuery({
    queryKey: ['superadmin-component-test-templates'],
    queryFn: async (): Promise<TemplatesResponse> => {
      const res = await fetch(`${API_URL}/superadmin/component-test-templates`, {
        credentials: 'include',
      });
      if (!res.ok) throw new Error('Failed to fetch templates');
      return res.json();
    },
  });

  const createMutation = useMutation({
    mutationFn: async (data: { elementType: ElementType; name: string; description?: string }) => {
      const res = await fetch(`${API_URL}/superadmin/component-test-templates`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(data),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body?.error ?? 'Failed to create template');
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['superadmin-component-test-templates'] });
      setShowAddForm(false);
      setNewName('');
      setNewDescription('');
    },
  });

  const updateMutation = useMutation({
    mutationFn: async (data: { id: string; name?: string; description?: string | null }) => {
      const res = await fetch(`${API_URL}/superadmin/component-test-templates/${data.id}`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ name: data.name, description: data.description }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body?.error ?? 'Failed to update template');
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['superadmin-component-test-templates'] });
      setEditingId(null);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const res = await fetch(`${API_URL}/superadmin/component-test-templates/${id}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body?.error ?? 'Failed to delete template');
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['superadmin-component-test-templates'] });
    },
  });

  const currentTemplates = templatesQuery.data?.templates?.[selectedType] ?? [];

  const startEditing = (template: ComponentTestTemplate) => {
    setEditingId(template.id);
    setEditName(template.name);
    setEditDescription(template.description ?? '');
  };

  const cancelEditing = () => {
    setEditingId(null);
    setEditName('');
    setEditDescription('');
  };

  const saveEditing = () => {
    if (!editingId || !editName.trim()) return;
    updateMutation.mutate({
      id: editingId,
      name: editName.trim(),
      description: editDescription.trim() || null,
    });
  };

  const handleCreate = () => {
    if (!newName.trim()) return;
    createMutation.mutate({
      elementType: selectedType,
      name: newName.trim(),
      description: newDescription.trim() || undefined,
    });
  };

  const columns: ColumnDef<StockFeatures, ComponentTestTemplate>[] = [
    {
      accessorKey: 'sortOrder',
      header: '#',
      size: 50,
      cell: ({ row }) => <Text size="sm">{row.original.sortOrder + 1}</Text>,
    },
    {
      accessorKey: 'name',
      header: 'Test Name',
      cell: ({ row }) => {
        if (editingId === row.original.id) {
          return (
            <TextField
              value={editName}
              onChange={(value) => setEditName(String(value))}
              aria-label="Test name"
              autoFocus
            />
          );
        }
        return <Text weight="medium">{row.original.name}</Text>;
      },
    },
    {
      accessorKey: 'description',
      header: 'Description',
      cell: ({ row }) => {
        if (editingId === row.original.id) {
          return (
            <TextField
              value={editDescription}
              onChange={(value) => setEditDescription(String(value))}
              aria-label="Description"
            />
          );
        }
        return (
          <Text size="sm" className="text-muted-foreground">
            {row.original.description || '—'}
          </Text>
        );
      },
    },
    {
      id: 'actions',
      header: '',
      size: 100,
      cell: ({ row }) => {
        if (editingId === row.original.id) {
          return (
            <Flex gap="xs">
              <Button
                variant="ghost"
                size="sm"
                onPress={saveEditing}
                isDisabled={!editName.trim() || updateMutation.isPending}
              >
                <Check size={16} />
              </Button>
              <Button variant="ghost" size="sm" onPress={cancelEditing}>
                <X size={16} />
              </Button>
            </Flex>
          );
        }
        return (
          <Flex gap="xs">
            <Button variant="ghost" size="sm" onPress={() => startEditing(row.original)}>
              <PencilSimple size={16} />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onPress={() => {
                if (confirm(`Delete "${row.original.name}"?`)) {
                  deleteMutation.mutate(row.original.id);
                }
              }}
              isDisabled={deleteMutation.isPending}
            >
              <Trash size={16} className="text-destructive" />
            </Button>
          </Flex>
        );
      },
    },
  ];

  return (
    <Card padding="lg">
      <CardHeader>
        <Flex align="center" justify="between" wrap gap="md">
          <div>
            <Heading level={3}>Component Test Templates</Heading>
            <Text size="sm" className="text-muted-foreground mt-1">
              Global test case templates per element type. These templates are used to generate
              exhaustive test cases for discovered UI components.
            </Text>
          </div>
          {templatesQuery.isLoading && <LoadingOrbit size="sm" />}
        </Flex>
      </CardHeader>
      <CardContent>
        <Flex direction="column" gap="lg">
          {/* Element Type Selector */}
          <Flex align="center" gap="md" wrap>
            <Text weight="medium">Element Type:</Text>
            <Select
              items={ELEMENT_TYPE_OPTIONS}
              selectedKey={selectedType}
              onSelectionChange={(key) => setSelectedType(key as ElementType)}
              aria-label="Select element type"
            />
            <Box className="ml-auto">
              <Button onPress={() => setShowAddForm(!showAddForm)} variant="secondary">
                <Plus size={16} />
                Add Template
              </Button>
            </Box>
          </Flex>

          {/* Add Form */}
          {showAddForm && (
            <Box className="border border-border rounded-md p-4 bg-muted/40">
              <Heading level={4} className="mb-3">
                Add Test Template for {ELEMENT_TYPE_LABELS[selectedType]}
              </Heading>
              <Flex direction="column" gap="md">
                <TextField
                  label="Test Name"
                  placeholder="e.g., Submit empty form"
                  value={newName}
                  onChange={(value) => setNewName(String(value))}
                  isRequired
                />
                <TextField
                  label="Description"
                  placeholder="What this test verifies..."
                  value={newDescription}
                  onChange={(value) => setNewDescription(String(value))}
                />
                <Flex gap="sm">
                  <Button
                    onPress={handleCreate}
                    isDisabled={!newName.trim() || createMutation.isPending}
                  >
                    {createMutation.isPending ? 'Creating...' : 'Create'}
                  </Button>
                  <Button
                    variant="ghost"
                    onPress={() => {
                      setShowAddForm(false);
                      setNewName('');
                      setNewDescription('');
                    }}
                  >
                    Cancel
                  </Button>
                  {createMutation.isError && (
                    <Text size="sm" className="text-destructive">
                      {createMutation.error?.message}
                    </Text>
                  )}
                </Flex>
              </Flex>
            </Box>
          )}

          <Divider />

          {/* Templates Table */}
          <Box>
            <Heading level={4} className="mb-3">
              {ELEMENT_TYPE_LABELS[selectedType]} Tests ({currentTemplates.length})
            </Heading>

            {templatesQuery.isLoading ? (
              <Flex align="center" justify="center" className="py-8">
                <LoadingOrbit size="lg" />
              </Flex>
            ) : templatesQuery.isError ? (
              <Text className="text-destructive">
                {templatesQuery.error instanceof Error
                  ? templatesQuery.error.message
                  : 'Failed to load templates'}
              </Text>
            ) : currentTemplates.length === 0 ? (
              <EmptyState
                title={`No test templates for ${ELEMENT_TYPE_LABELS[selectedType]} yet.`}
                action={
                  <Button variant="link" onPress={() => setShowAddForm(true)}>
                    Add the first template
                  </Button>
                }
              />
            ) : (
              <DataTable columns={columns} data={currentTemplates} />
            )}
          </Box>

          {/* Summary of all types */}
          <Divider />
          <Box>
            <Heading level={4} className="mb-3">
              Summary
            </Heading>
            <Flex gap="md" wrap>
              {ELEMENT_TYPE_OPTIONS.map((opt) => {
                const count = templatesQuery.data?.templates?.[opt.id as ElementType]?.length ?? 0;
                return (
                  <Box
                    key={opt.id}
                    className={`px-3 py-2 border rounded-md cursor-pointer hover:bg-muted/60 ${
                      selectedType === opt.id ? 'border-primary bg-primary/10' : 'border-border'
                    }`}
                    onClick={() => setSelectedType(opt.id as ElementType)}
                  >
                    <Text weight="medium">{opt.label}</Text>
                    <Text size="sm" className="text-muted-foreground">
                      {count} test{count !== 1 ? 's' : ''}
                    </Text>
                  </Box>
                );
              })}
            </Flex>
          </Box>
        </Flex>
      </CardContent>
    </Card>
  );
}
